import { QuoteResponseDto } from './../models/quote.dto';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EvcDto } from '@app/shared/modules/evc-api/models/evc';
import { Product } from '@app/shared/modules/evc-api/models/product';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ImageInfo } from 'src/app/shared/IFE/components/file-picker/models/imageInfo.interface';
import { environment } from '../../../../../environments/environment';
import { Car, CarDto, CarModel } from '../models/car';
import { Quote } from '../models/quote';
import { Simulation } from '../models/simulation';
import { SolarInverterBrand } from '../models/solar-inverter-brand';
import { CreateQuoteDto } from '../models/create-quote.dto';
import { ContactDto } from '../models/contact.dto';

const BUFFER_SIZE = 1;

@Injectable({
  providedIn: 'root',
})
export class EvcApiService {
  private quote$: BehaviorSubject<Quote> = new BehaviorSubject<Quote>(null);
  private cars$: BehaviorSubject<Car[]> = new BehaviorSubject<Car[]>(null);
  private solarInverterBrands$: BehaviorSubject<SolarInverterBrand[]> = new BehaviorSubject<SolarInverterBrand[]>(null);
  private products$: Observable<Product[]>;

  constructor(private http: HttpClient) {}

  simulate(data: Simulation): Observable<Simulation> {
    return this.http.post<Simulation>(`${environment.evcApi}/api/simulations`, data);
  }

  getSimulation(simId: string): Observable<Simulation> {
    return this.http.get<Simulation>(`${environment.evcApi}/api/simulations/${simId}`);
  }

  getQuote(id: string): Observable<Quote> {
    this.http.get<Quote>(`${environment.evcApi}/api/quotes/${id}`).subscribe({
      next: (value) => this.quote$.next(value),
      error: (error) => this.quote$.error(error),
    });

    return this.quote$;
  }

  saveQuote(quote: Quote): Observable<Quote> {
    return this.http.post<Quote>(`${environment.evcApi}/api/quotes`, quote);
  }

  updateQuote(id: string, quote: Quote): Observable<Quote> {
    this.http.put<Quote>(`${environment.evcApi}/api/quotes/${id}`, quote).subscribe({
      next: (value) => this.quote$.next(value),
      error: (error) => this.quote$.error(error),
    });

    return this.quote$;
  }

  getCarBrands(): Observable<Car[]> {
    if (!this.cars$.value) {
      this.http
        .get<CarDto>(`${environment.evcApi}/api/cars`)
        .pipe(map((value: CarDto) => value.items))
        .subscribe({
          next: (value) => this.cars$.next(value),
          error: (error) => this.cars$.error(error),
        });
    }

    return this.cars$.asObservable();
  }

  getCarModels(id: number): Observable<CarModel[]> {
    const existingCar = this.cars$.value.find((car) => car.id === id);
    if (existingCar && existingCar.models) {
      return of(existingCar.models);
    }

    return this.http.get<CarModel[]>(`${environment.evcApi}/api/cars/${id}/models`).pipe(
      map((carModels) => {
        this.cars$.value.filter((car) => car.id === id).forEach((car) => (car.models = carModels));
        return carModels;
      })
    );
  }

  uploadImage(id: string, formData: FormData): Observable<ImageInfo> {
    return this.http.post<ImageInfo>(`${environment.evcApi}/api/quotes/${id}/upload`, formData);
  }

  deleteImage(id: string, name: string): Observable<void> {
    return this.http.delete<void>(`${environment.evcApi}/api/quotes/${id}/images/${name}`);
  }

  continueLater(id: string): Observable<any> {
    return this.http.post<any>(`${environment.evcApi}/api/quotes/${id}/continue-later`, null);
  }

  getSolarInverterBrands(): Observable<SolarInverterBrand[]> {
    if (!this.solarInverterBrands$.value) {
      this.http.get<SolarInverterBrand[]>(`${environment.evcApi}/api/solar-inverter-brands`).subscribe({
        next: (value) => this.solarInverterBrands$.next(value),
        error: (error) => this.solarInverterBrands$.error(error),
      });
    }

    return this.solarInverterBrands$;
  }

  getProducts(): Observable<Product[]> {
    if (!this.products$) {
      this.products$ = this.http.get<EvcDto<Product>>(`${environment.smeEvcApi}/api/products`).pipe(
        map((dto) => dto.items),
        shareReplay(BUFFER_SIZE)
      );
    }
    return this.products$;
  }

  createQuote(quote: CreateQuoteDto) {
    return this.http.post<QuoteResponseDto>(`${environment.smeEvcApi}/api/quotes`, quote);
  }

  createContactChargingCard(contact: ContactDto) {
    return this.http.post(`${environment.smeEvcApi}/api/contact/card`, contact);
  }

  createContactChargingStation(contact: ContactDto) {
    return this.http.post(`${environment.smeEvcApi}/api/contact/charging-station`, contact);
  }
}
